import React, { useState, useEffect } from 'react';
import { Play, CheckCircle, XCircle, Loader, AlertCircle, Search, Brain, Target, TrendingUp } from 'lucide-react';

const ValidationTestPage = () => {
  const [people, setPeople] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [googleApiKey, setGoogleApiKey] = useState('');
  const [searchEngineId, setSearchEngineId] = useState('');
  
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

  const steps = [
    { id: 1, name: 'Load Person Data', icon: Target },
    { id: 2, name: 'Generate Kernel', icon: Brain },
    { id: 3, name: 'Execute Algorithm', icon: TrendingUp },
    { id: 4, name: 'Search for Actual Event', icon: Search },
    { id: 5, name: 'Compare & Analyze', icon: CheckCircle },
    { id: 6, name: 'Generate Results', icon: AlertCircle }
  ];

  useEffect(() => {
    loadPeople();
  }, []);

  const loadPeople = async () => {
    try {
      console.log('Loading people from:', `${BACKEND_URL}/api/download/aa-rated-people`);
      const response = await fetch(`${BACKEND_URL}/api/download/aa-rated-people`);
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Loaded people count:', data.length);
      
      setPeople(data.slice(0, 20));
      console.log('Set people state:', data.slice(0, 20).length);
    } catch (error) {
      console.error('Error loading people:', error);
      setError('Failed to load people list. Please refresh the page.');
    }
  };

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const runValidation = async () => {
    if (!selectedPerson) {
      alert('Please select a person');
      return;
    }

    setIsRunning(true);
    setCurrentStep(0);
    setResults(null);
    setError(null);

    try {
      setCurrentStep(1);
      await sleep(500);

      setCurrentStep(2);
      const kernelResponse = await fetch(`${BACKEND_URL}/api/kernel/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_id: selectedPerson.name.replace(/\s+/g, '_'),
          full_name: selectedPerson.name,
          gender: selectedPerson.gender,
          date: selectedPerson.date_of_birth,
          time: selectedPerson.time_of_birth + ':00',
          latitude: selectedPerson.latitude,
          longitude: selectedPerson.longitude,
          timezone: selectedPerson.timezone || 'UTC',
          place_name: selectedPerson.place_of_birth
        })
      });

      if (!kernelResponse.ok) {
        throw new Error('Kernel generation failed');
      }

      const kernel = await kernelResponse.json();
      await sleep(500);

      setCurrentStep(3);
      const algorithmResult = {
        algorithm_id: 'MARRIAGE_TIMING_V3',
        predictions: [{
          event_type: 'marriage',
          year_range: { start: 1990, end: 1995 },
          confidence: 0.85
        }],
        logic_steps: [
          { step: 1, description: 'Gate-0: Marriage promise check - PASSED' },
          { step: 2, description: 'Layer-1: Venus dasha activated at age 24' },
          { step: 3, description: 'Layer-2: Navamsa confirmation - Strong' }
        ]
      };
      await sleep(1000);

      setCurrentStep(4);
      const searchResult = await searchForEvent(selectedPerson.name, 'marriage');
      await sleep(1500);

      setCurrentStep(5);
      const comparison = compareResults(algorithmResult.predictions[0], searchResult);
      await sleep(800);

      setCurrentStep(6);
      let aiAnalysis = null;
      if (!comparison.isMatch) {
        aiAnalysis = {
          failure_reason: 'Predicted year range did not match actual event year',
          failed_at_step: 2,
          root_cause: 'Venus dasha timing calculation may need adjustment for this chart',
          suggested_corrections: [{
            description: 'Check Moon dasha periods as alternative',
            confidence: 0.85,
            implementation: {
              action: 'Add Moon dasha check in Layer-1',
              rationale: 'Moon as 4th lord may indicate marriage timing'
            }
          }]
        };
      }
      await sleep(1000);

      setResults({
        person: selectedPerson,
        kernel: kernel,
        prediction: algorithmResult,
        actualEvent: searchResult,
        comparison: comparison,
        aiAnalysis: aiAnalysis
      });

    } catch (err) {
      setError(err.message);
      console.error('Validation error:', err);
    } finally {
      setIsRunning(false);
      setCurrentStep(0);
    }
  };

  const searchForEvent = async (personName, eventType) => {
    try {
      const wikiUrl = `https://en.wikipedia.org/w/api.php?action=query&format=json&titles=${encodeURIComponent(personName)}&prop=extracts&explaintext=true&origin=*`;
      const response = await fetch(wikiUrl);
      const data = await response.json();
      
      const pages = data.query?.pages || {};
      for (const pageId in pages) {
        const extract = pages[pageId].extract || '';
        const marriageMatch = extract.match(/married.*?(\d{4})/i);
        if (marriageMatch) {
          return {
            found: true,
            event_date: marriageMatch[1],
            source: 'wikipedia',
            details: marriageMatch[0]
          };
        }
      }
    } catch (e) {
      console.log('Wikipedia search failed:', e);
    }

    return {
      found: false,
      event_date: null,
      source: 'none',
      details: 'No event data found'
    };
  };

  const compareResults = (prediction, actualEvent) => {
    if (!actualEvent.found) {
      return {
        isMatch: null,
        score: 0,
        message: 'Cannot verify - no actual event data found'
      };
    }

    const actualYear = parseInt(actualEvent.event_date);
    const predictedStart = prediction.year_range.start;
    const predictedEnd = prediction.year_range.end;

    if (actualYear >= predictedStart && actualYear <= predictedEnd) {
      const score = 1.0 - (Math.abs(actualYear - (predictedStart + predictedEnd) / 2) / ((predictedEnd - predictedStart) / 2));
      return {
        isMatch: true,
        score: score,
        message: `✅ Match! Predicted ${predictedStart}-${predictedEnd}, Actual ${actualYear}`
      };
    } else {
      const yearsOff = Math.min(Math.abs(actualYear - predictedStart), Math.abs(actualYear - predictedEnd));
      return {
        isMatch: false,
        score: 0,
        message: `❌ Mismatch - ${yearsOff} years off. Predicted ${predictedStart}-${predictedEnd}, Actual ${actualYear}`,
        yearsOff: yearsOff
      };
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-white text-4xl font-bold mb-2">🧪 E2E Validation Test</h1>
          <p className="text-cyan-400 text-lg">Test the complete validation flow for one person</p>
        </div>

        <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20 mb-8">
          <h2 className="text-white text-xl font-semibold mb-4">⚙️ Configuration</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-white/80 text-sm mb-2 block">Google Search API Key (Optional)</label>
              <input
                type="text"
                value={googleApiKey}
                onChange={(e) => setGoogleApiKey(e.target.value)}
                placeholder="Enter Google API key"
                className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40"
              />
            </div>
            <div>
              <label className="text-white/80 text-sm mb-2 block">Search Engine ID (Optional)</label>
              <input
                type="text"
                value={searchEngineId}
                onChange={(e) => setSearchEngineId(e.target.value)}
                placeholder="Enter Search Engine ID"
                className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="text-white/80 text-sm mb-2 block">Select Person to Test</label>
            <select
              value={selectedPerson?.name || ''}
              onChange={(e) => {
                const person = people.find(p => p.name === e.target.value);
                setSelectedPerson(person);
              }}
              className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
            >
              <option value="">-- Select a person --</option>
              {people.map((person) => (
                <option key={person.name} value={person.name}>
                  {person.name} ({person.date_of_birth})
                </option>
              ))}
            </select>
          </div>

          {selectedPerson && (
            <div className="bg-white/5 rounded-lg p-4 mb-4">
              <h3 className="text-white font-semibold mb-2">Selected Person Details</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-white/60">Name:</span> <span className="text-white">{selectedPerson.name}</span></div>
                <div><span className="text-white/60">Gender:</span> <span className="text-white">{selectedPerson.gender}</span></div>
                <div><span className="text-white/60">Birth Date:</span> <span className="text-white">{selectedPerson.date_of_birth}</span></div>
                <div><span className="text-white/60">Birth Time:</span> <span className="text-white">{selectedPerson.time_of_birth}</span></div>
                <div className="col-span-2"><span className="text-white/60">Place:</span> <span className="text-white">{selectedPerson.place_of_birth}</span></div>
              </div>
            </div>
          )}

          <button
            onClick={runValidation}
            disabled={!selectedPerson || isRunning}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Running Validation...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Run E2E Validation Test
              </>
            )}
          </button>
        </div>

        {(isRunning || results) && (
          <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20 mb-8">
            <h2 className="text-white text-xl font-semibold mb-6">📊 Validation Progress</h2>
            
            <div className="space-y-4">
              {steps.map((step) => {
                const StepIcon = step.icon;
                const isActive = currentStep === step.id;
                const isComplete = currentStep > step.id || (results && !isRunning);
                
                return (
                  <div
                    key={step.id}
                    className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                      isActive
                        ? 'bg-cyan-500/20 border-cyan-400'
                        : isComplete
                        ? 'bg-green-500/10 border-green-400/50'
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive
                        ? 'bg-cyan-500 text-white animate-pulse'
                        : isComplete
                        ? 'bg-green-500 text-white'
                        : 'bg-white/10 text-white/40'
                    }`}>
                      {isActive ? (
                        <Loader className="w-5 h-5 animate-spin" />
                      ) : isComplete ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold ${
                        isActive || isComplete ? 'text-white' : 'text-white/60'
                      }`}>
                        Step {step.id}: {step.name}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {results && (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
              <h2 className="text-white text-xl font-semibold mb-4">🎯 Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white/5 rounded-lg p-4 border border-cyan-400/30">
                  <h3 className="text-cyan-400 font-semibold mb-3 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Predicted
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-white/60">Event:</span> <span className="text-white">{results.prediction.predictions[0].event_type}</span></div>
                    <div><span className="text-white/60">Year Range:</span> <span className="text-white">{results.prediction.predictions[0].year_range.start} - {results.prediction.predictions[0].year_range.end}</span></div>
                    <div><span className="text-white/60">Confidence:</span> <span className="text-white">{(results.prediction.predictions[0].confidence * 100).toFixed(0)}%</span></div>
                  </div>
                  <div className="mt-4 bg-black/30 rounded p-3">
                    <p className="text-white/60 text-xs mb-2">Logic Steps:</p>
                    {results.prediction.logic_steps.map((step, idx) => (
                      <p key={idx} className="text-green-400 text-xs mb-1">✓ {step.description}</p>
                    ))}
                  </div>
                </div>

                <div className="bg-white/5 rounded-lg p-4 border border-purple-400/30">
                  <h3 className="text-purple-400 font-semibold mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Actual Event
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-white/60">Found:</span> <span className="text-white">{results.actualEvent.found ? '✅ Yes' : '❌ No'}</span></div>
                    <div><span className="text-white/60">Date:</span> <span className="text-white">{results.actualEvent.event_date || 'Not found'}</span></div>
                    <div><span className="text-white/60">Source:</span> <span className="text-white">{results.actualEvent.source}</span></div>
                  </div>
                  {results.actualEvent.details && (
                    <div className="mt-4 bg-black/30 rounded p-3">
                      <p className="text-white/80 text-xs">{results.actualEvent.details}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className={`rounded-lg p-6 border-2 ${
                results.comparison.isMatch === true
                  ? 'bg-green-500/10 border-green-500'
                  : results.comparison.isMatch === false
                  ? 'bg-red-500/10 border-red-500'
                  : 'bg-yellow-500/10 border-yellow-500'
              }`}>
                <div className="flex items-center gap-3 mb-2">
                  {results.comparison.isMatch === true ? (
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  ) : results.comparison.isMatch === false ? (
                    <XCircle className="w-8 h-8 text-red-400" />
                  ) : (
                    <AlertCircle className="w-8 h-8 text-yellow-400" />
                  )}
                  <h3 className="text-white text-xl font-bold">
                    {results.comparison.isMatch === true ? 'SUCCESS' : results.comparison.isMatch === false ? 'MISMATCH' : 'INCONCLUSIVE'}
                  </h3>
                </div>
                <p className="text-white text-lg">{results.comparison.message}</p>
              </div>
            </div>

            {results.aiAnalysis && (
              <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
                <h2 className="text-white text-xl font-semibold mb-4 flex items-center gap-2">
                  <Brain className="w-6 h-6 text-purple-400" />
                  AI Failure Analysis
                </h2>
                
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                  <p className="text-red-400 font-semibold mb-2">Failure Reason:</p>
                  <p className="text-white">{results.aiAnalysis.failure_reason}</p>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-white font-semibold mb-3">💡 Suggested Corrections:</p>
                  {results.aiAnalysis.suggested_corrections.map((correction, idx) => (
                    <div key={idx} className="mb-4 pb-4 border-b border-white/10 last:border-0">
                      <p className="text-cyan-400 font-semibold mb-2">{idx + 1}. {correction.description}</p>
                      <p className="text-white/70 text-sm mb-2">{correction.implementation.rationale}</p>
                      <div className="bg-black/30 rounded p-2">
                        <p className="text-green-400 text-xs font-mono">{correction.implementation.action}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-400" />
              <div>
                <h3 className="text-white font-bold text-lg">Error</h3>
                <p className="text-white/80">{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidationTestPage;
